import { useCallback, useState } from 'react';
import { ReactComponent as SwipeUpIcon } from '../../icons/Icon-AI-Scanner-SwipeUp.svg';
import { ReactComponent as TapIcon } from '../../icons/Icon-AI-Scanner-Tap.svg';
import useLocalize from '../../utils/hooks/useLocalize';

const STEP_SWIPE_UP = 1;
const STEP_HASHTAG = 2;

// Stable DOM id for the callout text. Referenced by `aria-labelledby` on the
// dialog wrapper so screen readers announce the current step's callout as
// the dialog's accessible name. Safe as a fixed id because the coach mark is
// a single-instance component gated by `showCoachMark` in AiScannerContainer.
const CALLOUT_TEXT_ID = 'ai-scanner-coach-mark-callout-text';

/**
 * Renders the bold segment inside the step 1 callout. Extracted so that
 * `useLocalize` can pass it as a `<b>` chunk renderer to `intl.formatMessage`.
 *
 * @param {React.ReactNode} chunks - Text captured between `<b>` and `</b>` in
 *   the localized message. Rendered inside a bold span so the middle portion
 *   of the step 1 callout stands out per the Figma design.
 * @returns {JSX.Element}
 */
const renderBoldChunk = (chunks) => (
  <span className="ai-scanner-coach-mark-callout-text-bold">{chunks}</span>
);

/**
 * AiScannerCoachMark
 *
 * Two-step onboarding overlay layered on top of the existing AI Scanner main
 * blank overlay. Guides first-time users through the two ways to start a
 * search on mobile.
 *
 * Step 1 — highlights the swipe-up area with the callout
 *   「上にスワイプして、ハッシュタグで直接検索してみましょう！」and a 「次へ」button.
 *   The middle segment 「ハッシュタグで」is rendered in bold via a react-intl
 *   `<b>` rich-text chunk (see [Figma Coach Mark_1](https://www.figma.com/design/5yEaLMUqwhtVxKsWQKEh3P?node-id=5271-13575)).
 * Step 2 — spotlights the "#ハッシュタグを選択する" link with the text
 *   「会話中でも検索できます！」and an 「AI Chatbot をはじめる」button that fires
 *   the `onComplete` callback provided by the parent container.
 *
 * Layout matches the Figma design:
 *   - Full-screen `rgba(0, 0, 0, 0.4)` backdrop with a radial-gradient elliptical
 *     cutout that "spotlights" the swipe area (step 1) or hashtag link (step 2).
 *   - White 240px-wide callout box with a small triangle pointer targeting the
 *     spotlight.
 *   - Gesture icon (upward hand for step 1, tap ripples for step 2) rendered
 *     to the right of the callout, over the cutout.
 *
 * Navigation is button-only. There are no page dots and no swipe gesture. The
 * parent container is responsible for reusing the existing ✕ close control
 * and marking the onboarding "seen" flag.
 *
 * @param {object} props
 * @param {() => void} props.onComplete - Called when the user taps
 *   「AI Chatbot をはじめる」on step 2.
 * @param {?number} [props.hashtagTargetY] - STORY-024 (#734): the hashtag
 *   link's centre in overlay pixels, which step 2's spotlight aims at.
 *   `null`/omitted leaves the stylesheet's fallback in place.
 * @returns {JSX.Element}
 */
export const AiScannerCoachMark = ({ onComplete, hashtagTargetY }) => {
  const localize = useLocalize();
  const [step, setStep] = useState(STEP_SWIPE_UP);

  const handleNext = useCallback(() => {
    setStep(STEP_HASHTAG);
  }, []);

  const handleStart = useCallback(() => {
    if (typeof onComplete === 'function') {
      onComplete();
    }
  }, [onComplete]);

  const isStep1 = step === STEP_SWIPE_UP;
  const stepModifier = isStep1 ? 'swipe-up' : 'hashtag';
  const buttonId = isStep1
    ? 'ai.scanner.onboarding.step1.next'
    : 'ai.scanner.onboarding.step2.start';
  const handleClick = isStep1 ? handleNext : handleStart;

  // Both steps use rich text formatting to bold specific segments via the
  // react-intl `<b>` chunk renderer.
  const calloutContent = isStep1
    ? localize('ai.scanner.onboarding.step1.callout', { b: renderBoldChunk })
    : localize('ai.scanner.onboarding.step2.callout', { b: renderBoldChunk });

  return (
    <div
      className="ai-scanner-coach-mark"
      /*
        STORY-024 (#734): the step-2 spotlight, hand and callout are all
        positioned off this one measured value — the hashtag link's centre in
        overlay pixels, taken by AiScannerContainer. Hard-coding it put the
        link 38px outside the lit ellipse at 320px wide in English, because
        how far down the link sits depends on how much the greeting and
        caution above it wrap. Omitted while unmeasured so the stylesheet's
        own fallback applies.
      */
      style={
        hashtagTargetY === null || hashtagTargetY === undefined
          ? undefined
          : { '--hashtag-target-y': `${hashtagTargetY}px` }
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby={CALLOUT_TEXT_ID}
    >
      <div
        className={`ai-scanner-coach-mark-spotlight ai-scanner-coach-mark-spotlight--${stepModifier}`}
        aria-hidden="true"
      />
      <div
        className={`ai-scanner-coach-mark-hand ai-scanner-coach-mark-hand--${stepModifier}`}
        aria-hidden="true"
      >
        {isStep1 ? (
          <SwipeUpIcon
            className="ai-scanner-coach-mark-hand-icon"
            aria-hidden="true"
          />
        ) : (
          <TapIcon
            className="ai-scanner-coach-mark-hand-icon"
            aria-hidden="true"
          />
        )}
      </div>
      <div
        className={`ai-scanner-coach-mark-callout ai-scanner-coach-mark-callout--${stepModifier}`}
      >
        <p id={CALLOUT_TEXT_ID} className="ai-scanner-coach-mark-callout-text">
          {calloutContent}
        </p>
        <button
          autoFocus
          type="button"
          className="ai-scanner-coach-mark-button"
          onClick={handleClick}
        >
          {localize(buttonId)}
        </button>
      </div>
    </div>
  );
};

export default AiScannerCoachMark;
